import React, { useCallback, useRef, useState } from 'react';
import { Animated, View, StyleSheet, StyleProp, ViewStyle, ImageResizeMode } from 'react-native';
import { useThemeColors } from '@/hooks/shared/useThemeColors';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';

interface FadeInImageProps {
  uri: string;
  /** Sizing/radius for the toned wrapper; the image fills it. */
  style?: StyleProp<ViewStyle>;
  resizeMode?: ImageResizeMode;
  /** e.g. "Photo 2 of 3". Omit for decorative images. */
  accessibilityLabel?: string;
  testID?: string;
}

/**
 * Image over a glass placeholder tone that fades in once loaded, so photos
 * never pop in. Reduced motion snaps to full opacity; a failed load keeps
 * the tone (layout holds, no broken-image glyph).
 */
export const FadeInImage: React.FC<FadeInImageProps> = ({
  uri,
  style,
  resizeMode = 'cover',
  accessibilityLabel,
  testID,
}) => {
  const { colors } = useThemeColors();
  const reduceMotion = useReducedMotion();
  const opacity = useRef(new Animated.Value(0)).current;
  const [failed, setFailed] = useState(false);

  const handleLoad = useCallback(() => {
    if (reduceMotion) {
      opacity.setValue(1);
      return;
    }
    Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }).start();
  }, [reduceMotion, opacity]);

  const handleError = useCallback(() => setFailed(true), []);

  return (
    <View
      style={[styles.wrap, { backgroundColor: colors.glass.buttonBg }, style]}
      testID={testID}
    >
      {!failed && (
        <Animated.Image
          source={{ uri }}
          style={[StyleSheet.absoluteFill, { opacity }]}
          resizeMode={resizeMode}
          onLoad={handleLoad}
          onError={handleError}
          accessible={!!accessibilityLabel}
          accessibilityLabel={accessibilityLabel}
        />
      )}
    </View>
  );
};

FadeInImage.displayName = 'FadeInImage';

const styles = StyleSheet.create({
  wrap: { overflow: 'hidden' },
});
